import React, {FunctionComponent, useCallback, useEffect, useMemo, useState} from 'react';
import stylesPage from '../../../../pages/Pages.module.scss';
import {useGridColumnPersist} from "../../../../hooks/useGridColumnPersist";
import {notifications} from "../../../../services";
import {useFormatter} from "../../../../hooks";
import {
  ICreateCustomerInvoice,
  ICustomerInvoice,
  InvoiceStatus
} from "../../../../model/api/customers/customer-quikcbook-invoices";
import {billingService} from "../../../../services/api/billing";
import {ICustomer} from "../../../../model";
import {Button} from "@progress/kendo-react-buttons";
import {fileExcelIcon, filePdfIcon} from "@progress/kendo-svg-icons";
import {Grid, GridColumn, GridDataStateChangeEvent, GridNoRecords} from "@progress/kendo-react-grid";
import {State} from '@progress/kendo-data-query';
import moment from 'moment';
import styles from '../BillingSection.module.scss'
import ViewInQBIcon from '../../../../assets/ViewInQBIcon.svg';
import ViewInQBDisabledIcon from '../../../../assets/ViewInQBDisabledIcon.svg';
import {downloadFile} from "../../../../utils/downloadFile";
import CommonModal from "../../../common/modal/CommonModal";

import {BillingMode} from "../../../../model/api/customers/billing-mode";
import {hasNexResponse} from "../../../../model/api/nexParkInstance/instancesChecker";
import {Spinner} from "../../../common";
import {IBillingTerm} from "../../../../model/api/customers/billing-term";
import {quickBookExternalService} from "../../../../services/api/quickBookExternal";
import GenerateInvoiceForm from "./GenerateInvoiceForm";
import {useBillingInvoices} from "./UseBillingInvoices";

interface Props {
  customer: ICustomer;
  hasBillingAccess: boolean;
}

export enum BillingModalType {
  GenerateInvoice,
  GenerateAttachment,
}

const initialDataState: any = {
  skip: 0,
  take: 25,
  sort: [],
  filter: { logic: 'and', filters: [] },
};

const INVOICE_ICON_VIEW_STATUS = [InvoiceStatus.CreatedSuccess, InvoiceStatus.CreatedWithWarnings];

export const BillingInvoicesSection: FunctionComponent<Props> = ({ customer, hasBillingAccess }) => {
  const customerId = customer.id;
  const formatter = useFormatter();
  const [dataState, setDataState] = useState<State>(initialDataState);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeModal, setActiveModal] = useState<BillingModalType>(BillingModalType.GenerateInvoice);
  const [generateServiceTarget, setGenerateServiceTarget] = useState<BillingMode>(hasNexResponse(customer) ? BillingMode.NexResponse : BillingMode.NexPark);
  const [comment, setComment] = useState('');
  const [selectedDueDate, setSelectedDueDate] = useState<Date>(new Date());
  const [invoiceTerm, setInvoiceTerm] = useState<string | undefined>('');
  const [billingTerms, setBillingTerms] = useState<Array<IBillingTerm>>([]);
  const { gridData, isLoading, loadGridData, setPendingIds } = useBillingInvoices(customerId, dataState);

  useEffect(() => {
    quickBookExternalService.getBillingTerms().then((res: IBillingTerm[]) => {
      setBillingTerms(res);
    })
  },[])

  const openQuickBookPage = (invoiceUrl: string) => {
    if (invoiceUrl === '' || invoiceUrl === null || invoiceUrl === undefined) {
      return;
    }
    window.open(invoiceUrl, '_blank');
  }
  const openGenerateModal = (modalType: BillingModalType) => {
    setActiveModal(modalType);
    const lastDayPrevMonth = moment().subtract(1, 'months').endOf('month').toDate();

    const defaultMode = hasNexResponse(customer) ? BillingMode.NexResponse : BillingMode.NexPark;
    const defaultTerm = defaultMode === BillingMode.NexResponse
        ? customer.nexResponseBillingSettings?.externalBillingSalesTermId
        : customer.nexParkBillingSettings?.externalBillingSalesTermId;

    setInvoiceTerm(defaultTerm);
    setSelectedDueDate(lastDayPrevMonth);
    setIsModalOpen(true);
  }

  const closeForm = () => {
    setIsModalOpen(false);
    setComment('');
    setSelectedDueDate(moment().startOf('month').toDate());
    setGenerateServiceTarget(hasNexResponse(customer) ? BillingMode.NexResponse : BillingMode.NexPark);
  };
  const handleGenerateServiceTargetChange = (billingMode: BillingMode) => {
    setGenerateServiceTarget(billingMode);
    setInvoiceTerm(
      billingMode === BillingMode.NexResponse
        ? customer.nexResponseBillingSettings?.externalBillingSalesTermId
        : customer.nexParkBillingSettings?.externalBillingSalesTermId,
    );
  };
  const generateCreature = () => {
    if (activeModal === BillingModalType.GenerateInvoice) {
      generateInvoice();
    }
    if (activeModal === BillingModalType.GenerateAttachment) {
      generateAttachment();
    }
  }

  const isBillingSettingsOff = (customer: ICustomer, billingMode: BillingMode) => {
    if (billingMode === BillingMode.NexPark) {
      return !customer.nexParkBillingSettings?.integrationEnabled;
    }
    if (billingMode === BillingMode.NexResponse) {
      return !customer.nexResponseBillingSettings?.integrationEnabled;
    }
    return true;
  }
  const downloadPdf = async (invoiceId: number, qbId: string) => {
    notifications.success('Invoice downloading started...');
    try {
      const data = await billingService.getInvoiceDocument(customerId, invoiceId);

      downloadFile(data, `Invoice-QB-${qbId}.pdf`, 'application/pdf');

      notifications.success(`Invoice-${qbId} downloaded successfully`);
    } catch (error) {
      console.error('Invoice download failed', error);
      notifications.error('Could not download PDF');
    }
  }
  const downloadExcel = async (invoice: ICustomerInvoice) => {
    notifications.success('Attachment downloading started...');
    try {
      const data = await billingService.getInvoiceAttachment(customerId, invoice.id);
      const excelType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

      const billingPeriod = invoice.dueDate ? moment(invoice.dueDate).format('MMM YYYY') : 'Unknown Period';

      downloadFile(data, `NXPK - ${customer?.name} ${billingPeriod}`, excelType);

      notifications.success(`Attachment downloaded successfully.`);
    } catch (error) {
      console.error('Attachment download failed', error);
      notifications.error('Failed to download attachment. Please try again or contact the team.');
    }
  }

  const generateAttachment = useCallback(async () => {
    notifications.success('Attachment generation started...');

    try {
      const billingPeriod = moment(selectedDueDate).startOf('month').format('YYYY-MM-DD');
      const response = await billingService.createAttachment(customerId, billingPeriod, generateServiceTarget);

      if (response && response.id && response.status === InvoiceStatus.InProgress) {
        setPendingIds((prev: number[]) => [...prev, response.id]);
      }

      if (response && response.statusDescription) {
        notifications.info(response.statusDescription);
      }

      closeForm();
      loadGridData();
    } catch (error) {
      notifications.error('Failed to start generation');
    }

  }, [customerId, loadGridData, generateServiceTarget, selectedDueDate]);

  const generateInvoice = useCallback(async () => {
    const billingPeriod = moment(selectedDueDate).startOf('month').format('YYYY-MM-DD');
    const payload: ICreateCustomerInvoice = {
      dueDate: billingPeriod,
      comment: comment,
      salesTermId: invoiceTerm
    };

    notifications.success('Invoice generation started...');

    try {
      const response = await billingService.createInvoice(customerId, payload, generateServiceTarget);

      if (response && response.id && response.status === InvoiceStatus.InProgress) {
        setPendingIds((prev: number[]) => [...prev, response.id]);
      }

      if (response && response.statusDescription) {
        notifications.info(response.statusDescription);
      }

      closeForm();
      loadGridData();
    } catch (error) {
      notifications.error('Failed to start generation');
    }
  }, [customerId, loadGridData, selectedDueDate, comment, generateServiceTarget, invoiceTerm]);

  const baseColumns = useMemo(() => [
    {
      field: 'quickBookInvoiceId',
      title: 'Invoices',
      width: '200px',
      filterable: false,
      cell: (props: any) => <td>{props?.dataItem?.quickBookInvoiceId ? `QB-${props?.dataItem?.quickBookInvoiceId}` : ''}</td>,
    },
    {
      field: 'dueDate',
      title: 'Period',
      filterable: false,
      cell: (props: any) => <td>
        {props.dataItem.dueDate
            ? moment(props.dataItem.dueDate).format('MMM YYYY')
            : '—'}
      </td>,
    },
    {
      field: 'generatedBy',
      filterable: false,
      title: 'Generated By',
    },
    {
      field: 'createDate',
      title: 'Date',
      filterable: false,
      format: '{0:MM/dd/yyyy HH:mm}',
      cell: (props: any) => <td>{moment(props.dataItem.createDate).fromNow()}</td>,
    },
    {
      field: 'service',
      title: 'Service',
      filterable: false,
    },
    ...(hasBillingAccess ? [{
      field: 'actions',
      title: 'Actions',
      width: '400px',
      filterable: false,
      sortable: false,
      cell: (props: any) => {
        const item: ICustomerInvoice = props.dataItem;
        const isProcessing = item.status === InvoiceStatus.InProgress;

        if (isProcessing) {
          return (
              <td>
                <div className={styles.spinnerContainer}>
                  <Spinner spinnerSmall={true} />
                </div>
              </td>
          );
        }

        const hasQB = !!item.quickBookInvoiceId;
        const hasPdf = hasQB && INVOICE_ICON_VIEW_STATUS.includes(item.status);
        const hasExcel = !!item.attachmentFileName;
        const isFailed = item.status === InvoiceStatus.Failed;

        return (
            <td>
              <div className={styles.gridActionPosition}>
                <div>
                  {hasQB && (
                      <Button
                          type="button"
                          className={!item.invoiceUrl ? `${styles.viewQBBtnDisabled} action-grid-button` : `${styles.viewQBBtn} action-grid-button`}
                          disabled={!item.invoiceUrl}
                          onClick={() => openQuickBookPage(item.invoiceUrl)}
                      >
                    <span>
                      View in QB{' '}
                      <img
                          src={item.invoiceUrl ? ViewInQBIcon : ViewInQBDisabledIcon}
                          alt=""
                      />
                    </span>
                      </Button>
                  )}
                </div>
                
                <div>
                  {hasPdf && (
                      <Button
                          type="button"
                          className="action-grid-button"
                          svgIcon={filePdfIcon}
                          onClick={() => downloadPdf(item.id, item.quickBookInvoiceId!)}
                      >
                        Invoices
                      </Button>
                  )}
                </div>

                <div>
                  {hasExcel ? (
                      <Button
                          type="button"
                          className="action-grid-button"
                          svgIcon={fileExcelIcon}
                          onClick={() => downloadExcel(item)}
                      >
                        Excel
                      </Button>
                  ) : isFailed ? (
                      <div className={styles.failedInvoiceBox} title={item.statusDescription}>
                        Failed
                      </div>
                  ) : null}
                </div>
              </div>
            </td>
        );
      }
    }] : []),


  ], [formatter, isLoading]);

  const {
    columns,
    onColumnResize,
    onColumnReorder
  } = useGridColumnPersist(`customer-billing-invoices-grid-order-and-size`, baseColumns);

  const onDataStateChange = useCallback((event: GridDataStateChangeEvent) => {
    setDataState(event.dataState);
  }, []);

  const gridKey = useMemo(() => columns.map(c => c.field).join(','), [columns]);

  return (
      <div>
        <div className={styles.invoicesTablePanel}>
          <div className={styles.panelTitle}>Invoices and Files</div>
          <div className={styles.buttonsPanel}>
            <Button
                onClick={() => {openGenerateModal(BillingModalType.GenerateAttachment)}}
                type="button"
            >
              Generate Attachment
            </Button>
            <Button
                onClick={() => {openGenerateModal(BillingModalType.GenerateInvoice)}}
                type="button"
                themeColor={'primary'}
            >
              Generate Invoice
            </Button>
          </div>
        </div>


        <Grid
            key={gridKey}
            style={{ height: '500px' }}
            data={gridData.data}
            total={gridData.total}
            {...dataState}
            onDataStateChange={onDataStateChange}

            resizable={true}
            reorderable={true}
            onColumnResize={onColumnResize}
            onColumnReorder={onColumnReorder}

            sortable={true}
            pageable={{
              buttonCount: 4,
              pageSizes: [5, 10, 25],
            }}
        >
          <GridNoRecords>
            {isLoading ? 'Loading...' : 'No records found.'}
          </GridNoRecords>

          {columns.map((col, idx) => (
              <GridColumn key={col.field || idx} {...col} />
          ))}
        </Grid>
        <CommonModal
            isOpen={isModalOpen}
            simpleHeader={true}
            onClose={() => closeForm()}
            title={activeModal === BillingModalType.GenerateInvoice ? `Generate Invoice` : 'Generate Attachment'}
        >
          <div style={{ padding: '20px' }} key={isModalOpen ? 'open' : 'closed'}>
            <GenerateInvoiceForm
                serviceType={generateServiceTarget}
                billingModalType={activeModal}
                onServiceChange={handleGenerateServiceTargetChange}
                comment={comment}
                onCommentChange={setComment}
                dueDate={selectedDueDate}
                onDateChange={(date) => setSelectedDueDate(date)}
                onTermChange={(id) => setInvoiceTerm(id)}
                customer={customer}
                invoiceTerm={invoiceTerm}
                billingTerms={billingTerms}
            />
          </div>
          <div className={styles.modalFooter}>
            <Button type='button' onClick={() => closeForm()}>
              Cancel
            </Button>
            <Button type='button' form='main-customer-form'
                    disabled={isLoading || isBillingSettingsOff(customer, generateServiceTarget)}
                    themeColor={'primary'} onClick={() => generateCreature()}>
              Generate
            </Button>
          </div>
        </CommonModal>
      </div>
  );
};
